import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, HostListener, Inject, Input, OnInit, Optional, Output, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ActionIconDef, Grid, GridActionIconClickEvent, GridConfig, GridExportRequestedEvent, GridHyperlinkClickEvent, GridToolbarActionClickEvent } from '@inavista/components/grid';
import { SharedService } from 'projects/shared/src/public-api';
import { APP_CONFIG, AppConfig } from 'projects/shared/src/lib/app-config.token';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { CommonService } from '../../../shared/services/common.service';
import { LeadsPopupComponent } from '../leads-popup/leads-popup.component';
import { LeadsService } from '../leads.service';
import { tabsModel, ModuleNames } from '../../../shared/models/app.model';
import { UserRoles } from '../../../shared/constants/app.constants';
import { defaultProspectData } from '../../business-partners/model/prospects-data';
import { ProspectsPopupComponent } from '../../business-partners/prospects-popup/prospects-popup.component';
import { OpportunitiesPopupComponent } from '../../opportunities/opportunities-popup/opportunities-popup.component';
import { HomeService } from '../../home/home.service';
import { ToDosPopupComponent } from '../../todos/to-dos-popup/to-dos-popup.component';
import { ADD_LEAD_ACTION, buildLeadsActionIcons, buildLeadsGridConfig, buildLeadsToolbarActions, leadActionDisabledReason, LeadsGridContext } from './leads.grid.config';

// Leads grid on @inavista/components (leads.grid.config.ts): the Sales Leads page, the Sales dashboard's Leads pod popup
// (dialogData.isOpenedFromDashboard) and RIMS Service's Leads page (fromServiceApp: the lead opens in the Service popup
// through openLeadsPopupEvent). Same actions, popups and permissions as the Syncfusion grid it replaces.
@Component({
    selector: 'app-leads-grid',
    templateUrl: './leads-grid.component.html',
    styleUrl: './leads-grid.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LeadsGridComponent implements OnInit {
  @Input() fromServiceApp = false;
  @Output() openLeadsPopupEvent = new EventEmitter<{ id: string | null; tab: string }>();

  // Read by RIMS Service's wrapper to refresh the list after its popup.
  @ViewChild('leadsGrid') grid?: Grid;
  @ViewChild('businessPartnerCell', { static: true }) businessPartnerCell!: TemplateRef<unknown>;

  private readonly destroyRef = inject(DestroyRef);

  // Null until the role permissions are known (actions, Add and Excel depend on them).
  readonly gridConfig = signal<GridConfig | null>(null);
  readonly actionIcons = signal<ActionIconDef[]>([]);
  readonly showLoader = signal(false);

  // The grid fills the window from where it starts down to the page frame's bottom padding, as Service Orders.
  private static readonly BottomFramePx = 15;
  readonly gridPageHeight = signal(window.innerHeight - 120);
  private gridPageEl?: HTMLElement;
  // The wrapper appears once the permissions are known, so it is measured when it does.
  @ViewChild('gridPage') set gridPage(ref: ElementRef<HTMLElement> | undefined) {
    this.gridPageEl = ref?.nativeElement;
    if (this.gridPageEl) setTimeout(() => this.fitGridToWindow());
  }

  @HostListener('window:resize')
  fitGridToWindow() {
    const top = this.gridPageEl?.getBoundingClientRect().top;
    if (top === undefined) return;
    this.gridPageHeight.set(Math.max(300, Math.floor(window.innerHeight - top - LeadsGridComponent.BottomFramePx)));
  }

  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    public dialog: MatDialog,
    private commonService: CommonService,
    private leadsService: LeadsService,
    private route: ActivatedRoute,
    private homeService: HomeService,
    private sharedService: SharedService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() public dialogRef: MatDialogRef<any>
  ) {}

  ngOnInit(): void {
    this.commonService.getRolePermission('leadsGrid', '', 'lead').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.initGrid(res?.result ?? []),
      // As before: without the rules the grid still opens, with the actions a missing rule allows.
      error: () => {
        this.initGrid([]);
        this.commonService.showNotification('error', 'Your leads permissions could not be loaded, so some actions are hidden. Refresh the page to try again.');
      },
    });

    // ?primaryId=<lead>&showComments=true (a comment notification) opens that lead with its comments, as before.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const primaryId = params.get('primaryId');
      // After this first check: opening a dialog (or, in Service, the wrapper's) inside it is not allowed.
      if (primaryId && params.get('showComments') === 'true') {
        setTimeout(() => this.openLeadsPopup(primaryId, tabsModel.details));
        setTimeout(() => this.showComments(primaryId), 500);
      }
    });
  }

  private initGrid(rules: any[]) {
    const permissions: LeadsGridContext['permissions'] = {};
    rules.forEach(rule => permissions[rule.ruleAction] = { visible: rule.visible, enable: rule.enable });
    const user = this.commonService.getUSER();
    const ctx: LeadsGridContext = {
      saleApiUrl: this.config.saleApiUrl,
      mode: this.dialogData?.isOpenedFromDashboard ? 'dashboard' : this.fromServiceApp ? 'service' : 'sales',
      dashboard: { status: this.dialogData?.filteredStatus, regions: this.dialogData?.regions, districts: this.dialogData?.districts },
      permissions,
      canActAsSalesRep: !!user?.canActAsSalesRep,
      canExport: user?.role !== UserRoles.salesRep,
      businessPartnerCell: this.businessPartnerCell,
    };
    this.actionIcons.set(buildLeadsActionIcons(ctx));
    this.gridConfig.set({ ...buildLeadsGridConfig(ctx), toolbarActions: buildLeadsToolbarActions(ctx) });
  }

  onToolbarActionClick(event: GridToolbarActionClickEvent) {
    if (event.key === ADD_LEAD_ACTION) this.openLeadsPopup();
  }

  // Same file as before: the visible columns of the rows loaded, "Leads (<date>).xlsx".
  onExportRequested(event: GridExportRequestedEvent) {
    if (this.grid) exportInavistaGrid(this.grid, 'Leads ', event.rows, 'Leads');
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.openLeadsPopup(String(event.row['salesLeadId']));
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    const row: any = event.row;
    const reason = leadActionDisabledReason(event.iconKey, row);
    if (reason) { this.commonService.showNotification('info', reason); return; }
    switch (event.iconKey) {
      case 'details': this.openLeadsPopup(row.salesLeadId); return;
      case 'convert':
        this.commonService.showConfirmation('Are you sure, you want to convert this lead to opportunity?').then(result => {
          if (result.isConfirmed) this.onCreateOpportunityClick(row);
        });
        return;
      case 'map': this.onBPIdClick(row.soldToExtId || row.soldToBp); return;
      case 'links': this.openLeadsPopup(row.salesLeadId, tabsModel.links); return;
      // Opened after this click: the comments panel closes on any document click outside it.
      case 'comments': setTimeout(() => this.showComments(row.salesLeadId)); return;
      case 'attachments': this.openLeadsPopup(row.salesLeadId, tabsModel.attachments); return;
      case 'history': this.openLeadsPopup(row.salesLeadId, tabsModel.history); return;
    }
  }

  private showComments(salesLeadId: string) {
    this.sharedService.globalComments = {
      grid: { refresh: () => this.refresh(salesLeadId) } as any,
      module: ModuleNames.SalesLead, moduleId: salesLeadId, showComments: true,
    };
  }

  // Re-reads one lead in place, so the user keeps their place; the whole list when none is known.
  refresh(salesLeadId?: string | null) {
    if (!this.grid) return;
    if (!salesLeadId) { this.grid.refresh(); return; }
    this.grid.refreshRow(salesLeadId).catch(() =>
      this.commonService.showNotification('error', 'The list could not be updated. Please refresh the page.'));
  }

  onBPIdClick(soldToBP: string) {
    const dialogRef = this.dialog.open(ProspectsPopupComponent, {
      backdropClass: 'no-backdrop',
      disableClose: true,
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100%',
      height: '100%',
      enterAnimationDuration: '0',
      exitAnimationDuration: '0',
      data: {
        showProspectsPopup: true,
        prospectData: { ...defaultProspectData, soldToExtId: soldToBP },
        id: soldToBP,
        tab: tabsModel.details,
        isMaximized: true,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refresh());
  }

  openLeadsPopup(id: string = null, tab: string = tabsModel.details) {
    if (this.fromServiceApp) {
      this.openLeadsPopupEvent.emit({ id, tab });
      return;
    }
    const dialog = this.dialog.open(LeadsPopupComponent, {
      backdropClass: 'no-backdrop',
      disableClose: true,
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100%',
      height: '100%',
      enterAnimationDuration: '0',
      exitAnimationDuration: '0',
      autoFocus: false,
      data: {
        id: id,
        isMaximized: true,
        tab,
        fromServiceApp: this.fromServiceApp
      }
    });
    dialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result) {
        this.refresh();
        this.refreshPods();
      }
    });
  }

  private refreshPods() {
    this.homeService.hitRefreshPods({
      LeadStats_pod: true,
      OppStats_pod: true,
      PropStats_pod: true,
      SOStats_pod: true,
      NL_pod: true,
      UL_pod: true,
    });
  }

  onCreateOpportunityClick(data: any) {
    const assignedResourceId = data?.assignedResourceId || "";
    const assignedResourceName = data?.assignedResourceName || "";
    this.showLoader.set(true);
    this.commonService.onCreateOpportunityWithOpenTodosPrequisiteValidations(data?.mappedSoldToBP, data?.salesLeadId)
      .then((results) => {
        this.showLoader.set(false);
        switch (results.action) {
          case "openOpportunityPopup":
            if ([UserRoles.salesDistricMgr, UserRoles.salesRegionalMgr, UserRoles.salesAdmin].includes(this.commonService.getUSER()?.role) || this.commonService?.getUSER()?.canActAsSalesRep) {
              results.data.salesRepId = assignedResourceId;
              results.data.salesRepName = assignedResourceName;
            }
            this.openOpportunityPopup({ ...results?.data, ...data });
            break;
          case "openLeadsPopup":
            this.openLeadsPopup(results?.data);
            break;
          case "openTodosPopup":
            this.openToDoPopup(results?.data, tabsModel.details);
            break;
          case "error":
            this.commonService.showNotification("error", "Unable to perform this action!");
            break;
        }
      });
  }

  openToDoPopup(data?: any, tab: tabsModel = tabsModel.details, copyTodo?: boolean) {
    const dialogRef = this.dialog.open(ToDosPopupComponent, {
      disableClose: true,
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '80%',
      height: '100%',
      enterAnimationDuration: '0',
      exitAnimationDuration: '0',
      data: {
        copyTodo,
        isMaximized: true,
        tab,
        ...data
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.copyTodo) {
        this.openToDoPopup(result, tabsModel.details, true);
      }
    });
  }

  openOpportunityPopup(data) {
    const proceedOpenningOpportunityPopup = (data: any) => {
      const dialog = this.dialog.open(OpportunitiesPopupComponent, {
        backdropClass: 'no-backdrop',
        disableClose: true,
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100%',
        height: '100%',
        enterAnimationDuration: '0',
        exitAnimationDuration: '0',
        autoFocus: false,
        data: {
          id: data.id,
          isMaximized: true,
          type: 'direct',
          fromLeads: true,
          leadData: {
            salesLeadId: data.salesLeadId,
            source: data.source,
            creatorId: data.creatorId,
            creatorName: data.creatorName,
            leadCreatedDate: data.createdDate,
            leadCallNotes: data.notes,
            reasonNotes: data.reasonNotes,
            salesRepName: data.salesRepName,
            salesRepId: data.salesRepId,
            productLine: data.productCategory
          },
          bpData: {
            name: data?.mappedBPName,
            id: data?.mappedSoldToBP,
            address: data?.mappedBPAddress,
            email: data?.mappedBPEmail,
            contact: data?.mappedBPTelephone,
            city: data?.city,
            state: data?.stateProvince,
            zipCode: data?.zipCode,
            country: data?.country,
            countryId: data?.countryId ?? 1
          }
        }
      });
      dialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        if (result) {
          this.refresh();
          this.refreshPods();
          window.location.href = window.location.origin + '/opportunities';
        }
      });
    };
    if (data.leadIdFromLeadsModule) {
      this.leadsService.getLeadById(data.leadIdFromLeadsModule).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => proceedOpenningOpportunityPopup(res?.result)
      });
    } else {
      proceedOpenningOpportunityPopup(data);
    }
  }
}
